import { inject, Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import * as L from 'leaflet';
import { WebatlasTileLayer, WebatlasTileLayerTypes, webatlasTileLayer } from 'leaflet-webatlastile';
import { ServerResponse } from '../constants/server-response.enum';
import { MapLayer } from '../interfaces/map';

@Injectable()
export class MapOptionsService {
  private translate = inject(TranslateService);
  private zoomNeededControl?: L.Control;
  private activeMapTileLayer: MapLayer | undefined;

  addBaseControls(map: L.Map): void {
    this.switchMapView(map);
    L.control
      .zoom({
        position: 'bottomright',
      })
      .addTo(map);

    L.control
      .scale({
        position: 'bottomright',
        metric: true, // shows meters/km
        imperial: false, // hides miles
      })
      .addTo(map);
  }

  addLogoControl(map: L.Map): void {
    map.addControl(
      new (L.Control.extend({
        options: { position: 'bottomleft' },
        onAdd: () => {
          const div = L.DomUtil.create('div');
          div.className = 'promo-logo';
          const img = document.createElement('img');
          img.src = './assets/promo-logo.svg';
          img.style.maxWidth = '200px';
          img.style.maxHeight = '120px';
          img.style.width = 'auto';
          img.style.height = 'auto';
          img.style.pointerEvents = 'none';
          div.appendChild(img);
          return div;
        },
      }))(),
    );
  }

  switchMapView(map: L.Map, isAerialViewActive = false): void {
    const baseLayers = this.getMapBaseLayers();
    if (this.activeMapTileLayer) {
      map.removeLayer(this.activeMapTileLayer);
    }
    this.activeMapTileLayer = isAerialViewActive ? baseLayers.Aerial : baseLayers.Vector;
    map.addLayer(this.activeMapTileLayer);

    /** remove leaflet and norkart attribution */
    map.attributionControl.remove();
  }

  checkAndBuildZoomNeededControl(map: L.Map | undefined, serverResponse: ServerResponse): void {
    if (!map) return;

    if (serverResponse === ServerResponse.Ok && this.zoomNeededControl) {
      map.removeControl(this.zoomNeededControl);
      this.zoomNeededControl = undefined;
      return;
    }

    if (serverResponse === ServerResponse.ZoomLevelTooLow && !this.zoomNeededControl) {
      const controlToAdd = this.zoomNeededControl
        ? this.zoomNeededControl
        : this.getZoomNeededBoxControl();

      this.zoomNeededControl = controlToAdd;
      controlToAdd.addTo(map);
    }
  }

  private getZoomNeededBoxControl(): L.Control<L.ControlOptions> {
    const ScrollBoxControl = L.Control.extend({
      options: {
        position: 'topright',
      },

      onAdd: () => {
        const scrollBox = L.DomUtil.create('div', 'scroll-box-control');
        scrollBox.title = this.translate.instant('General.ZoomNeededInfoTitle');

        const html = `
          <div class="popup">
            <div class="popup__icon-content">
              <div class="popup__icon">
                <span class=dv-icons>zoom_in</span>
              </div>
            </div>
           <div>
          <div class="popup__content">
            <div class="popup__title dv-headline-md">
              ${this.translate.instant(
                'General.ZoomNeededInfoTitle',
              )}<span class="popup__title--tip">${this.translate.instant('General.Tip')}</span>
            </div>
          <div class="popup__text dv-body-md">
            ${this.translate.instant('General.ZoomNeededInfoBody')}<br />
            ${this.translate.instant(
              'General.Use',
            )} <span class="popup__text--key dv-body-md">+</span> ${this.translate.instant(
              'General.OrScrollToZoom',
            )}.
          </div>
        </div>
      </div>
    </div>`;
        scrollBox.innerHTML = html;
        return scrollBox;
      },
    });

    return new ScrollBoxControl();
  }

  private getMapBaseLayers(): {
    Vector: WebatlasTileLayer;
    Aerial: WebatlasTileLayer;
  } {
    const vector = webatlasTileLayer({
      apiKey: environment.leafletApiKey,
      mapType: WebatlasTileLayerTypes.VECTOR,
    });
    const aerial = webatlasTileLayer({
      apiKey: environment.leafletApiKey,
      mapType: WebatlasTileLayerTypes.AERIAL,
    });

    return {
      Vector: vector,
      Aerial: aerial,
    };
  }
}
